/**
 * 几何选区工具的手势与合成逻辑（M2-07 / DESIGN.md §6.2）。
 *
 * 纯计算，无 DOM：指针事件由 CanvasView 换算成文档坐标后喂进来，
 * 产出的 SelectionMask 由装配层写回 AppStore。修饰键语义照 Photoshop：
 * Shift 加选、Alt 减选，都不按住则替换。
 */
import { SelectionMask } from '../../engine/selection/mask';
import {
  fromEllipse,
  fromPolygon,
  fromRect,
  fromRoundedRect,
  intersect,
  subtract,
  union,
  type RectSpec,
} from '../../engine/selection/ops';

export interface DocPoint {
  readonly x: number;
  readonly y: number;
}

export type SelectionCombine = 'replace' | 'add' | 'subtract' | 'intersect';

/**
 * 修饰键语义照 Photoshop：Shift 加选、Alt 减选、Shift+Alt 交叉选区，
 * 都不按住时用 `defaultMode`——那是工具属性栏"新选区/添加/减去/交叉"
 * 四个模式按钮里当前选的那个（省略时是 'replace'，即没有模式按钮的工具
 * 保持原来的行为）。按修饰键永远是临时覆盖，不会改动按钮记的默认模式。
 */
export function combineFromModifiers(
  modifiers: {
    readonly shiftKey: boolean;
    readonly altKey: boolean;
  },
  defaultMode: SelectionCombine = 'replace',
): SelectionCombine {
  if (modifiers.shiftKey && modifiers.altKey) return 'intersect';
  if (modifiers.altKey) return 'subtract';
  if (modifiers.shiftKey) return 'add';
  return defaultMode;
}

/** 把新选区与现有选区按修饰键语义合成。 */
export function applyCombine(
  current: SelectionMask | null,
  next: SelectionMask,
  combine: SelectionCombine,
): SelectionMask {
  if (current === null) {
    // 没有选区时：减选/交叉都是"什么都没有"，新选区/添加则是 next 本身
    return combine === 'subtract' || combine === 'intersect'
      ? SelectionMask.empty(next.width, next.height)
      : next;
  }
  switch (combine) {
    case 'replace':
      return next;
    case 'add':
      return union(current, next);
    case 'subtract':
      return subtract(current, next);
    case 'intersect':
      return intersect(current, next);
  }
}

/** 拖拽出矩形：起点 + 当前点 → 归一化 RectSpec。 */
export class RectGesture {
  #start: DocPoint | null = null;
  #current: DocPoint | null = null;

  begin(point: DocPoint): void {
    this.#start = point;
    this.#current = point;
  }

  update(point: DocPoint): RectSpec | null {
    if (this.#start === null) return null;
    this.#current = point;
    return this.rect();
  }

  rect(): RectSpec | null {
    if (this.#start === null || this.#current === null) return null;
    const x = Math.min(this.#start.x, this.#current.x);
    const y = Math.min(this.#start.y, this.#current.y);
    return {
      x,
      y,
      width: Math.abs(this.#current.x - this.#start.x),
      height: Math.abs(this.#current.y - this.#start.y),
    };
  }

  /** 结束并复位。太小的拖拽（<1px）视为误触，返回 null。 */
  end(): RectSpec | null {
    const rect = this.rect();
    this.#start = null;
    this.#current = null;
    if (rect === null || rect.width < 1 || rect.height < 1) return null;
    return rect;
  }

  get active(): boolean {
    return this.#start !== null;
  }

  /** 拖拽起点（未归一化，带方向）——"固定比例"样式要知道拖拽方向才能
   * 决定往哪边延伸，`rect()`/`end()` 给的是已经取了 min/abs 的归一化
   * 结果，方向信息已经丢了，只能从这两个原始点重新算。 */
  get start(): DocPoint | null {
    return this.#start;
  }

  get current(): DocPoint | null {
    return this.#current;
  }
}

export type MarqueeStyle = 'normal' | 'fixedRatio' | 'fixedSize';

/**
 * 按选框工具属性栏的"样式"选项调整拖拽结果（参照 Photoshop）：
 * - 正常：原样返回归一化矩形，不调整。
 * - 固定比例：保持拖拽方向，取拖拽范围里更大的那个轴当基准，按
 *   `styleWidth:styleHeight` 换算另一轴——跟裁剪工具"比例"约束同一个
 *   思路（拖多远决定整体多大，比例决定长宽怎么分配）。
 * - 固定大小：完全忽略拖拽范围，在起点摆一个 `styleWidth×styleHeight`
 *   的矩形，起点是矩形左上角——真实 Photoshop 这个样式下点一下（不用
 *   真的拖出多大）就能落一个固定尺寸的选框。
 */
export function applyMarqueeStyle(
  start: DocPoint,
  current: DocPoint,
  style: MarqueeStyle,
  styleWidth: number,
  styleHeight: number,
): RectSpec {
  if (style === 'fixedSize') {
    return { x: start.x, y: start.y, width: Math.max(0, styleWidth), height: Math.max(0, styleHeight) };
  }
  const rawWidth = current.x - start.x;
  const rawHeight = current.y - start.y;
  if (style === 'fixedRatio' && styleWidth > 0 && styleHeight > 0) {
    const ratio = styleWidth / styleHeight;
    let width = rawWidth;
    let height = rawHeight;
    // 谁的"按比例应该有的跨度"更大，就以谁的拖拽范围为基准——避免出现
    // "两轴都要缩小才能凑比例"这种跟手感觉相反的结果
    if (Math.abs(rawWidth) >= Math.abs(rawHeight) * ratio) {
      height = Math.sign(rawHeight === 0 ? 1 : rawHeight) * (Math.abs(rawWidth) / ratio);
    } else {
      width = Math.sign(rawWidth === 0 ? 1 : rawWidth) * (Math.abs(rawHeight) * ratio);
    }
    return {
      x: Math.min(start.x, start.x + width),
      y: Math.min(start.y, start.y + height),
      width: Math.abs(width),
      height: Math.abs(height),
    };
  }
  return {
    x: Math.min(start.x, current.x),
    y: Math.min(start.y, current.y),
    width: Math.abs(rawWidth),
    height: Math.abs(rawHeight),
  };
}

/** 自由套索：收集拖拽轨迹，抬起时闭合成多边形。 */
export class FreehandGesture {
  #points: DocPoint[] = [];

  begin(point: DocPoint): void {
    this.#points = [point];
  }

  add(point: DocPoint): void {
    const last = this.#points.at(-1);
    if (last === undefined) return;
    // 距离阈值：指针事件密度远超需要，1px 内的点只增加多边形边数
    if (Math.hypot(point.x - last.x, point.y - last.y) >= 1) {
      this.#points.push(point);
    }
  }

  /** 结束并复位。不足三个点构不成面。 */
  end(): DocPoint[] | null {
    const points = this.#points;
    this.#points = [];
    return points.length >= 3 ? points : null;
  }

  get active(): boolean {
    return this.#points.length > 0;
  }

  /** 当前轨迹（拖拽期间的实时预览用，只读快照）。 */
  points(): readonly DocPoint[] {
    return this.#points;
  }
}

/**
 * 多边形套索：逐次点击加顶点，双击（或点回起点附近）闭合。
 * `addVertex` 返回 true 表示已闭合，调用方取 `end()` 的结果。
 */
export class PolygonGesture {
  #points: DocPoint[] = [];

  addVertex(point: DocPoint): boolean {
    const first = this.#points[0];
    // 点回起点 4px 内闭合（文档像素）
    if (first !== undefined && this.#points.length >= 3) {
      if (Math.hypot(point.x - first.x, point.y - first.y) <= 4) return true;
    }
    this.#points.push(point);
    return false;
  }

  end(): DocPoint[] | null {
    const points = this.#points;
    this.#points = [];
    return points.length >= 3 ? points : null;
  }

  get active(): boolean {
    return this.#points.length > 0;
  }

  /** 已落下的顶点（拖拽期间的实时预览用，只读快照）。 */
  points(): readonly DocPoint[] {
    return this.#points;
  }
}

/** 按工具形状栅格化矩形拖拽的结果。 */
export function marqueeMask(
  shape: 'rectangle' | 'ellipse',
  docWidth: number,
  docHeight: number,
  rect: RectSpec,
  /** 矩形选框的圆角半径（文档像素）；椭圆忽略。≤0 是普通直角矩形。 */
  radius = 0,
): SelectionMask {
  if (shape === 'ellipse') return fromEllipse(docWidth, docHeight, rect);
  return radius > 0 ? fromRoundedRect(docWidth, docHeight, rect, radius) : fromRect(docWidth, docHeight, rect);
}

/** 套索/多边形轨迹 → 蒙版。 */
export function lassoMask(
  docWidth: number,
  docHeight: number,
  points: readonly DocPoint[],
): SelectionMask {
  return fromPolygon(docWidth, docHeight, points);
}
